import type React from 'react'
import { GROUP_HEADER, HEADER_CLASSES } from '../../../lib/table-utils'

export const OptionChainHeader: React.FC<{ isVisible: (key: string) => boolean }> = ({ isVisible }) => {
    // Helper helper to reduce boilerplate
    const show = (key: string) => isVisible(key)

    return (
        <thead className="sticky top-0 z-50 group">
            {/* Top Row: Group Headers */}
            {/* Dynamic ColSpan Calculation */}

            {(() => {
                const callCols = [
                    'delta',
                    'gamma',
                    'theta',
                    'vega',
                    'oi',
                    'oi_change',
                    'volume',
                    'iv',
                    'change_p',
                    'ltp',
                    'combined_oi',
                    'combined_oi_change',
                    'combined_oi_change_p',
                    'vol_diff',
                    'oi_diff',
                ]
                const putCols = [
                    'combined_vol_change',
                    'combined_vol_change_p',
                    'combined_volume',
                    'ltp',
                    'change_p',
                    'iv',
                    'volume',
                    'oi_change',
                    'oi',
                    'vega',
                    'theta',
                    'gamma',
                    'delta',
                ]
                const callSpan = callCols.filter((c) => show(c)).length
                const putSpan = putCols.filter((c) => show(c)).length

                return (
                    <tr>
                        <th colSpan={callSpan} className={GROUP_HEADER.CALLS_SIDE}>
                            <div className={GROUP_HEADER.BADGE_CALLS}>CALLS</div>
                        </th>
                        <th className="p-2 text-center bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px]"></th>
                        <th colSpan={putSpan} className={GROUP_HEADER.PUTS_SIDE}>
                            <div className={GROUP_HEADER.BADGE_PUTS}>PUTS</div>
                        </th>
                    </tr>
                )
            })()}

            {/* Bottom Row: Column Headers - MUST MATCH BODY ORDER EXACTLY */}
            <tr>
                {/* CALLS SIDE */}
                {show('delta') && (
                    <GhostTh align="right" side="call">
                        Delta
                    </GhostTh>
                )}
                {show('gamma') && (
                    <GhostTh align="right" side="call">
                        Gamma
                    </GhostTh>
                )}
                {show('theta') && (
                    <GhostTh align="right" side="call">
                        Theta
                    </GhostTh>
                )}
                {show('vega') && (
                    <GhostTh align="right" side="call">
                        Vega
                    </GhostTh>
                )}
                {show('oi') && (
                    <GhostTh align="right" side="call">
                        OI (L)
                    </GhostTh>
                )}
                {show('oi_change') && (
                    <GhostTh align="right" side="call">
                        OI Chg
                    </GhostTh>
                )}
                {show('volume') && (
                    <GhostTh align="right" side="call">
                        Vol (Cr)
                    </GhostTh>
                )}
                {show('iv') && (
                    <GhostTh align="right" side="call">
                        IV
                    </GhostTh>
                )}
                {show('change_p') && (
                    <GhostTh align="right" side="call">
                        Chg%
                    </GhostTh>
                )}
                {show('ltp') && (
                    <GhostTh align="right" side="call">
                        LTP
                    </GhostTh>
                )}
                {show('combined_oi') && (
                    <GhostTh align="right" side="call">
                        Comb OI
                    </GhostTh>
                )}
                {show('combined_oi_change') && (
                    <GhostTh align="right" side="call">
                        Comb OI Chg
                    </GhostTh>
                )}
                {show('combined_oi_change_p') && (
                    <GhostTh align="right" side="call">
                        Comb OI Chg%
                    </GhostTh>
                )}
                {show('vol_diff') && (
                    <GhostTh align="center" side="neutral" color="blue">
                        Vol Diff
                    </GhostTh>
                )}
                {show('oi_diff') && (
                    <GhostTh align="center" side="neutral" color="blue">
                        OI Diff
                    </GhostTh>
                )}

                {/* STRIKE */}
                <th className={HEADER_CLASSES.STRIKE_STICKY}>STRIKE</th>

                {/* PUTS SIDE - Reverse Order relative to center? No, standard order in body: LTP, Chg, IV, Vol, OI Chg, OI, Vega... */}
                {show('combined_vol_change') && (
                    <GhostTh align="left" side="put">
                        Comb Vol Chg
                    </GhostTh>
                )}
                {show('combined_vol_change_p') && (
                    <GhostTh align="left" side="put">
                        Comb Vol Chg%
                    </GhostTh>
                )}
                {show('combined_volume') && (
                    <GhostTh align="left" side="put">
                        Comb Vol
                    </GhostTh>
                )}
                {show('ltp') && (
                    <GhostTh align="left" side="put">
                        LTP
                    </GhostTh>
                )}
                {show('change_p') && (
                    <GhostTh align="left" side="put">
                        Chg%
                    </GhostTh>
                )}
                {show('iv') && (
                    <GhostTh align="left" side="put">
                        IV
                    </GhostTh>
                )}
                {show('volume') && (
                    <GhostTh align="left" side="put">
                        Vol (Cr)
                    </GhostTh>
                )}
                {show('oi_change') && (
                    <GhostTh align="left" side="put">
                        OI Chg
                    </GhostTh>
                )}
                {show('oi') && (
                    <GhostTh align="left" side="put">
                        OI (L)
                    </GhostTh>
                )}
                {show('vega') && (
                    <GhostTh align="left" side="put">
                        Vega
                    </GhostTh>
                )}
                {show('theta') && (
                    <GhostTh align="left" side="put">
                        Theta
                    </GhostTh>
                )}
                {show('gamma') && (
                    <GhostTh align="left" side="put">
                        Gamma
                    </GhostTh>
                )}
                {show('delta') && (
                    <GhostTh align="left" side="put">
                        Delta
                    </GhostTh>
                )}
            </tr>
        </thead>
    )
}

interface GhostThProps {
    children: React.ReactNode
    align?: 'left' | 'right' | 'center'
    side?: 'call' | 'put' | 'neutral'
    color?: 'red' | 'green' | 'blue' | 'amber' | 'purple' | 'neutral' // Flexible colors
    onClick?: () => void
    className?: string
    colSpan?: number
    rowSpan?: number
    topOffset?: string
}

export const GhostTh: React.FC<GhostThProps> = ({
    children,
    align = 'center',
    side = 'neutral',
    color,
    onClick,
    className = '',
    colSpan,
    rowSpan,
    topOffset = '46px',
}) => {
    // Premium light-themed header styles
    let thBgClass = 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/80'
    let textClass = 'text-slate-400 dark:text-slate-500 font-black'

    // Side logic - Clean text colors
    if (side === 'call' || color === 'red') {
        textClass = 'text-rose-600 dark:text-rose-400 font-black'
    } else if (side === 'put' || color === 'green') {
        textClass = 'text-emerald-600 dark:text-emerald-400 font-black'
    } else if (color === 'blue' || color === 'purple') {
        textClass = 'text-indigo-600 dark:text-indigo-400 font-black'
    } else if (color === 'amber') {
        textClass = 'text-amber-600 dark:text-amber-400 font-black'
    }

    const alignClass = align === 'left' ? 'text-left pl-4' : align === 'right' ? 'text-right pr-4' : 'text-center'

    return (
        <th
            className={`p-0 sticky z-30 whitespace-nowrap ${thBgClass} ${className}`}
            onClick={onClick}
            colSpan={colSpan}
            rowSpan={rowSpan}
            style={{
                cursor: onClick ? 'pointer' : 'default',
                top: topOffset, // Fixed position below first header row
            }}
        >
            <div className={`py-2.5 ${alignClass}`}>
                <span className={`text-[10px] tracking-widest uppercase ${textClass}`}>
                    {children}
                </span>
            </div>
        </th>
    )
}
